/**
 * Client-side image compression via canvas.
 * Target: keep under maxBytes (default 2MB), prefer smaller for storage.
 */
export function compressImage(file, options = {}) {
  const {
    maxBytes = 2 * 1024 * 1024,
    maxWidth = 1600,
    maxHeight = 1600,
    mimeType = 'image/jpeg',
    qualityStart = 0.85,
  } = options;

  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('仅支持图片文件'));
      return;
    }
    if (file.size <= maxBytes && file.size < 400 * 1024) {
      // already small enough
      resolve(file);
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const ratio = Math.min(1, maxWidth / width, maxHeight / height);
      width = Math.max(1, Math.round(width * ratio));
      height = Math.max(1, Math.round(height * ratio));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      let quality = qualityStart;
      let blob = await canvasToBlob(canvas, mimeType, quality);

      while (blob && blob.size > maxBytes && quality > 0.4) {
        quality -= 0.1;
        blob = await canvasToBlob(canvas, mimeType, quality);
      }

      // If still too big, scale down further
      let scale = 0.85;
      while (blob && blob.size > maxBytes && scale > 0.35) {
        const w = Math.max(1, Math.round(width * scale));
        const h = Math.max(1, Math.round(height * scale));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        blob = await canvasToBlob(canvas, mimeType, Math.max(0.45, quality));
        scale -= 0.1;
      }

      if (!blob) {
        reject(new Error('图片压缩失败'));
        return;
      }
      if (blob.size > maxBytes) {
        reject(new Error('图片过大，请选择更小的图片（最大 2MB）'));
        return;
      }

      const name = (file.name || 'image').replace(/\.\w+$/, '') + '.jpg';
      resolve(new File([blob], name, { type: mimeType, lastModified: Date.now() }));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('无法读取图片'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}
